import { yupResolver } from "@hookform/resolvers/yup";
import { useDatabase } from "@nozbe/watermelondb/hooks";
import { useNavigation } from "@react-navigation/native";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { TextInput, TextInputProps } from "react-native";
import Product from "../../../../../../utilities/data/model/Product";
import { useDatabaseProductHelpers } from "../../../../../../utilities/product";
import { useScheduler } from "../../../../../../utilities/scheduler";
import {
  CurrentCountContextType,
  ScanMode,
  StockAdjusterFormValues,
  StockSelection,
  StockSelectionType,
  SubmitAdjustBy,
  UseStockAdjusterReturnType,
  setValueOptions,
  stockAdjusterValidationSchema,
} from "../../../../../../utilities/stock-count";
import { useStockCountSearchContext } from "../../../../../../utilities/stock-count/hooks";
import { useStockCounter } from "../../hooks";
import { isNumber } from "lodash";
import StockCountLine from "../../../../../../utilities/data/model/StockCountLine";
import { Q } from "@nozbe/watermelondb";

export const useStockAdjuster = (
  baseCurrentCountContext: Omit<CurrentCountContextType, "stockAdjuster">,
): UseStockAdjusterReturnType => {
  const { schedule } = useScheduler(100);

  const countInputRef = useRef<TextInput>(null);
  const searchInputRef = useRef<TextInput>(null);

  const searchContext = useStockCountSearchContext();

  const [searchFocused, setSearchFocused] = useState(false);

  const [selection, setSelection] =
    useState<TextInputProps["selection"]>(undefined);

  const { stockCount, stockSelection, updateStockSelection } =
    baseCurrentCountContext;

  const selectedStockCountId = stockCount.id;

  const [product, setProduct] = useState<Product | undefined>(undefined);

  const database = useDatabase();

  const navigation = useNavigation<any>();

  useEffect(() => {
    const productId = stockSelection?.stockIdentifier.productId;
    if (productId) {
      database.collections
        .get<Product>("products")
        .find(productId)
        .then((product) => {
          setProduct(product);
        })
        .catch(() => setProduct(undefined));
    }
  }, [stockSelection?.stockIdentifier.productId]);

  const isQuickScan = searchContext.scanMode === "quick";

  const methods = useForm<StockAdjusterFormValues>({
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: getDefaultFormValues(searchContext.scanMode),
    resolver: yupResolver(stockAdjusterValidationSchema),
  });

  useEffect(() => {
    methods.reset(getDefaultFormValues(searchContext.scanMode));
  }, [searchContext.scanMode]);

  useEffect(() => {
    setSelection(undefined);
  }, [searchContext.fullTextSearch.query]);

  const {
    formState: { isValid },
    getValues,
    handleSubmit,
    reset,
    setValue,
  } = methods;

  const queryRef = useRef(searchContext.fullTextSearch.query);

  queryRef.current = searchContext.fullTextSearch.query;

  const { countStockItem } = useStockCounter();
  const { findProductByStockNumber } = useDatabaseProductHelpers();

  function getDefaultFormValues(scanMode: ScanMode) {
    return scanMode === "quick" ? { counted: 0 } : { counted: 1 };
  }

  function handleChangeAdjustBy(value: string) {
    setValue("counted", value as any, setValueOptions);
  }

  function focusCountInput() {
    schedule(() => {
      countInputRef.current?.focus();
    });
  }

  function focusSearchInput() {
    schedule(() => {
      searchInputRef.current?.focus();
      setSearchFocused(true);
      setSelection({
        start: 0,
        end: queryRef.current.length,
      });
    });
  }

  const postSubmitSearchCallback = () => {
    if (isQuickScan) {
      focusSearchInput();
    } else {
      focusCountInput();
    }
  };

  const handleOnSubmitSearch = async () => {
    const query = searchContext.fullTextSearch.query;

    const shouldQuery =
      query?.length &&
      query !== product?.name &&
      query !== stockSelection?.stockIdentifier.barcode;

    if (shouldQuery) {
      // first check if the scanned/entered code matches an existing counted product
      // this was added to stop the "add new non-product" flow when scanning a barcode
      // that has already been added to the count as a product
      const maybeExistingStockCountLine = await database.collections
        .get<StockCountLine>("stock_count_lines")
        .query(
          Q.where("stock_count_id", stockCount.id),
          Q.where("barcode", query),
          Q.take(1),
        )
        .fetch()
        .then((res) => res[0]);

      if (maybeExistingStockCountLine) {
        await updateStockSelection({
          stockIdentifier: {
            productId: maybeExistingStockCountLine?.product?.id,
            barcode: maybeExistingStockCountLine.barcode,
          },
          triggerUpdatedAt: false,
          updateFormOnly: false,
          selectionType: StockSelectionType.SEARCH_SUBMIT,
        });
        postSubmitSearchCallback();
        return;
      }

      return findProductByStockNumber(query)
        .then(async (maybeProduct) => {
          if (!maybeProduct) {
            // no matching product found, allow non-product count
            return openNonProductCountScreen(query);
          }
          await updateStockSelection({
            stockIdentifier: {
              productId: maybeProduct.id,
              barcode: maybeProduct.stockNumber,
            },
            triggerUpdatedAt: false,
            updateFormOnly: false,
            selectionType: StockSelectionType.SEARCH_SUBMIT,
          });

          postSubmitSearchCallback();
        })
        .catch(console.log);
    } else if (stockSelection) {
      // still update selection to apply selection type

      updateStockSelection({
        ...stockSelection,
        triggerUpdatedAt: false,
        selectionType: StockSelectionType.SEARCH_SUBMIT,
        updateFormOnly: false,
      });

      postSubmitSearchCallback();
    }
  };

  const openNonProductCountScreen = (barcode: string) => {
    navigation.navigate("CountProductModalRootScreen", {
      screen: "CountNonProductModalScreen",
      params: {
        barcode: barcode,
      },
    });
  };

  const handleOnSubmitCount = () => {
    if (isQuickScan) {
      increaseAdjustBy();
    } else {
      // submit count
      handleSubmit(({ counted }) => {
        submitAdjustBy({
          stockIdentifier: stockSelection?.stockIdentifier,
          adjustBy: counted,
        });
      })();

      // refocus search input ready to scan again
      focusSearchInput();
    }
  };

  function increaseAdjustBy() {
    const existingAdjustBy = getValues("counted") || 0;
    const newAdjustBy = +existingAdjustBy + 1;
    setValue("counted", newAdjustBy, setValueOptions);
  }

  async function submitAdjustBy({ stockIdentifier, adjustBy }: SubmitAdjustBy) {
    const query = searchContext.fullTextSearch.query;
    if (
      !selectedStockCountId ||
      (!stockIdentifier?.barcode &&
        !stockIdentifier?.productId &&
        query.length <= 0)
    ) {
      return Promise.resolve();
    }

    if (stockIdentifier) {
      return countStockItem(selectedStockCountId, stockIdentifier, adjustBy)
        .then(() => {
          methods.reset();
          focusSearchInput();
        })
        .catch((e) => console.log(e));
    } else {
      return;
      return findProductByStockNumber(query).then((maybeProduct) => {
        if (maybeProduct) {
          return countStockItem(selectedStockCountId, { barcode: query }, 1)
            .then(() => {
              methods.reset();
              focusSearchInput();
            })
            .catch((e) => console.log(e));
        }
      });
    }
  }

  const updateForm = async (
    newStockSelection: StockSelection,
    previousStockSelection?: StockSelection,
  ) => {
    const currentSelectedStock = newStockSelection?.stockIdentifier;
    const previousSelectedStock = previousStockSelection?.stockIdentifier;

    if (!currentSelectedStock) return Promise.resolve();

    searchContext.fullTextSearch.setQuery(
      product?.name || product?.stockCode || "",
    );

    if (isQuickScan) {
      focusSearchInput();

      // Changed to use barcode; non-existing-products dont have a product id
      // so this would fail when clicking on a non-existing-product
      // CHANGE 2 26/2/25 - Was failing on first scan of a non-existing-product
      const isAutoSubmit =
        (previousSelectedStock && !previousSelectedStock.barcode) ||
        (previousSelectedStock &&
          previousSelectedStock?.barcode !== currentSelectedStock.barcode);

      if (isAutoSubmit) {
        await handleSubmit(({ counted }) => {
          if (counted === undefined || !isNumber(counted))
            return Promise.resolve();

          return submitAdjustBy({
            stockIdentifier: previousSelectedStock,
            adjustBy: counted,
          });
        })();
        reset(getDefaultFormValues(searchContext.scanMode));
      }

      if (newStockSelection.selectionType !== StockSelectionType.MANUAL_ENTRY) {
        increaseAdjustBy();
      }
    } else {
      focusCountInput();
    }
  };

  const isSubmitDisabled = !isValid || !stockSelection?.stockIdentifier;

  return {
    countInputRef,
    searchInputRef,
    methods,
    isSubmitDisabled,
    searchFocused,
    selection,
    setSearchFocused,
    updateForm,
    submitAdjustBy,
    increaseAdjustBy,
    handleChangeAdjustBy,
    handleOnSubmitSearch,
    handleOnSubmitCount,
  };
};
